import { Suspense } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import { Loader2 } from "lucide-react";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";
import { RequireAuth } from "@/auth/RequireAuth";
import { AppShell } from "@/shell/AppShell";

// Eager — first paint paths
import { Login } from "@/pages/Login";
import { Portfolio } from "@/pages/Portfolio";

// Lazy — per-route chunks
const Overview = lazy(() => import("@/pages/Overview").then((m) => ({ default: m.Overview })));
const BrandDashboard = lazy(() => import("@/pages/BrandDashboard").then((m) => ({ default: m.BrandDashboard })));
const Actions = lazy(() => import("@/pages/Actions").then((m) => ({ default: m.Actions })));
const ActionDetail = lazy(() => import("@/pages/ActionDetail").then((m) => ({ default: m.ActionDetail })));
const EmailEditor = lazy(() => import("@/pages/EmailEditor").then((m) => ({ default: m.EmailEditor })));
const AuditReport = lazy(() => import("@/pages/AuditReport").then((m) => ({ default: m.AuditReport })));
const Audits = lazy(() => import("@/pages/Audits").then((m) => ({ default: m.Audits })));
const AuditLog = lazy(() => import("@/pages/AuditLog").then((m) => ({ default: m.AuditLog })));
const AuditDetail = lazy(() => import("@/pages/AuditDetail").then((m) => ({ default: m.AuditDetail })));
const Flows = lazy(() => import("@/pages/Flows").then((m) => ({ default: m.Flows })));
const FlowStudio = lazy(() => import("@/pages/FlowStudio").then((m) => ({ default: m.FlowStudio })));
const Campaigns = lazy(() => import("@/pages/Campaigns").then((m) => ({ default: m.Campaigns })));
const CampaignStudio = lazy(() => import("@/pages/CampaignStudio").then((m) => ({ default: m.CampaignStudio })));
const Templates = lazy(() => import("@/pages/Templates").then((m) => ({ default: m.Templates })));
const CompetitorEmails = lazy(() => import("@/pages/CompetitorEmails").then((m) => ({ default: m.CompetitorEmails })));
const RemixStudio = lazy(() => import("@/pages/RemixStudio").then((m) => ({ default: m.RemixStudio })));
const ImportHtml = lazy(() => import("@/pages/ImportHtml").then((m) => ({ default: m.ImportHtml })));
const ImportHtmlReview = lazy(() => import("@/pages/ImportHtml").then((m) => ({ default: m.ImportHtmlReview })));
const Segments = lazy(() => import("@/pages/Segments").then((m) => ({ default: m.Segments })));
const Customers = lazy(() => import("@/pages/Customers").then((m) => ({ default: m.Customers })));
const Impact = lazy(() => import("@/pages/Impact").then((m) => ({ default: m.Impact })));
const CatalogInsights = lazy(() => import("@/pages/CatalogInsights").then((m) => ({ default: m.CatalogInsights })));
const Sms = lazy(() => import("@/pages/Sms").then((m) => ({ default: m.Sms })));
const Replenishment = lazy(() => import("@/pages/Replenishment").then((m) => ({ default: m.Replenishment })));
const Subjects = lazy(() => import("@/pages/Subjects").then((m) => ({ default: m.Subjects })));
const Synthesis = lazy(() => import("@/pages/Synthesis").then((m) => ({ default: m.Synthesis })));
// Hidden for now (2026-06-22): Composer page (import + route + AppShell nav item all commented).
// const Composer = lazy(() => import("@/pages/Composer").then((m) => ({ default: m.Composer })));
const Promotions = lazy(() => import("@/pages/Promotions").then((m) => ({ default: m.Promotions })));
const WeeklyReview = lazy(() => import("@/pages/WeeklyReview").then((m) => ({ default: m.WeeklyReview })));
const Goals = lazy(() => import("@/pages/Goals").then((m) => ({ default: m.Goals })));
const Monitors = lazy(() => import("@/pages/Monitors").then((m) => ({ default: m.Monitors })));
const BrandSettings = lazy(() => import("@/pages/BrandSettings").then((m) => ({ default: m.BrandSettings })));
const Onboard = lazy(() => import("@/pages/Onboard").then((m) => ({ default: m.Onboard })));
const OrgSettings = lazy(() => import("@/pages/OrgSettings").then((m) => ({ default: m.OrgSettings })));
const Admin = lazy(() => import("@/pages/Admin").then((m) => ({ default: m.Admin })));
const Playbooks = lazy(() => import("@/pages/Playbooks").then((m) => ({ default: m.Playbooks })));
const Translate = lazy(() => import("@/pages/Translate").then((m) => ({ default: m.Translate })));
const TranslateReview = lazy(() => import("@/pages/TranslateReview").then((m) => ({ default: m.TranslateReview })));
const Chat = lazy(() => import("@/pages/Chat").then((m) => ({ default: m.Chat })));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite").then((m) => ({ default: m.AcceptInvite })));

function ChunkFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg">
      <Loader2 className="animate-spin text-text-muted" size={20} />
    </div>
  );
}

/**
 * Brand landing. A brand always opens on its `dashboard`, scoped to its primary
 * (largest) market by default — `useRegion` resolves `brand.primary_region`
 * when no `?region=` is present. "All markets" (the Executive Overview) is the
 * explicit opt-in via the region picker / the "All Markets" nav item, never the
 * default landing.
 */
function BrandIndexRedirect() {
  return <Navigate to="dashboard" replace />;
}

export function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<ChunkFallback />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* Public — the invite email's accept link. Must sit outside
              RequireAuth and before the "*" catch-all, which otherwise
              swallows it into /dashboard without accepting. */}
          <Route path="/invite/:token" element={<AcceptInvite />} />

          <Route element={<RequireAuth><AppShell /></RequireAuth>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Portfolio />} />
            <Route path="/onboard" element={<Onboard />} />
            <Route path="/settings" element={<OrgSettings />} />
            <Route path="/admin" element={<Admin />} />

            <Route path="/brand/:slug">
              <Route index element={<BrandIndexRedirect />} />
              <Route path="overview" element={<Overview />} />
              <Route path="dashboard" element={<BrandDashboard />} />
              <Route path="actions" element={<Actions />} />
              <Route path="actions/:actionId" element={<ActionDetail />} />
              <Route path="actions/:actionId/emails/:messageKey/edit" element={<EmailEditor />} />
              <Route path="audit" element={<AuditReport />} />
              <Route path="audits" element={<Audits />} />
              <Route path="audits/:auditId" element={<AuditDetail />} />
              <Route path="audit-log" element={<AuditLog />} />
              <Route path="flows" element={<Flows />} />
              <Route path="flows/:flowId" element={<FlowStudio />} />
              <Route path="campaigns" element={<Campaigns />} />
              <Route path="campaigns/:campaignId" element={<CampaignStudio />} />
              <Route path="templates" element={<Templates />} />
              <Route path="competitor-emails" element={<CompetitorEmails />} />
              <Route path="competitor-emails/remix/:remixId" element={<RemixStudio />} />
              <Route path="import-html" element={<ImportHtml />} />
              <Route path="import-html/:importId" element={<ImportHtmlReview />} />
              <Route path="translate" element={<Translate />} />
              <Route path="translate/drafts/:id" element={<TranslateReview />} />
              <Route path="segments" element={<Segments />} />
              {/* Performance + Reports retired 2026-06-18 — folded into the
                  dashboard (per-flow/campaign breakdown + attribution donut).
                  Redirect so existing links/bookmarks still land. */}
              <Route path="performance" element={<Navigate to="../dashboard" replace />} />
              <Route path="customers" element={<Customers />} />
              <Route path="catalog" element={<CatalogInsights />} />
              <Route path="sms" element={<Sms />} />
              <Route path="next-order" element={<Replenishment />} />
              {/* Renamed replenishment → next-order (2026-06-23) to match the
                  "Next Order" UI label. Redirect so existing links/bookmarks
                  still land. The data endpoint stays /api/.../replenishment. */}
              <Route path="replenishment" element={<Navigate to="../next-order" replace />} />
              <Route path="impact" element={<Impact />} />
              <Route path="subjects" element={<Subjects />} />
              <Route path="synthesis" element={<Synthesis />} />
              {/* Hidden for now (2026-06-22): Composer page. Restore by uncommenting this route + the lazy import above + the AppShell nav item. */}
              {/* <Route path="composer" element={<Composer />} /> */}
              <Route path="promotions" element={<Promotions />} />
              <Route path="weekly-review" element={<WeeklyReview />} />
              <Route path="goals" element={<Goals />} />
              <Route path="monitors" element={<Monitors />} />
              <Route path="playbooks" element={<Playbooks />} />
              <Route path="reports" element={<Navigate to="../dashboard" replace />} />
              {/* Setup page hidden (2026-06-19) — redirect like `reports`. */}
              <Route path="setup" element={<Navigate to="../dashboard" replace />} />
              <Route path="settings" element={<BrandSettings />} />
              {/* Full-page persistent chat. Also serves the FloatingChat
                  widget body via ?embedded=1 (iframed by ChatPanel.tsx). */}
              <Route path="chat" element={<Chat />} />
              {/* // retention divergence (user-approved): ads-iq has no left-nav
                  Chat item — see BRAND_NAV in shell/AppShell.tsx. */}
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
